// GeneralInfo.tsx

import React, {useEffect, useRef, useState} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel} from '@mui/material';
import RadioLine from 'Core/components/elements/RadioLine';
import Button from 'Core/components/elements/Button/Button';
import TextField from "Core/components/elements/TextField/TextField";
import TimeZonePicker from 'Core/components/elements/TimeZonePicker';
import {axios, DOMAIN_URL} from 'Auth/axiosConfig';
import {FC} from "WideLayout/Layouts";
import Head from "Core/components/Head";
import {useTheme} from "Theme/ThemeContext";

interface FormData {
    age: number;
    therapy_experience: string;
    timezone: string;
    client_gender: string;
}

const calculateAge = (birthDateStr: string): number => {
    const today = new Date();
    const birthDate = new Date(birthDateStr);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const GeneralInfo: React.FC = () => {
    const {
        register,
        control,
        handleSubmit,
        setValue,
        reset,
        watch,
        formState: {errors, isValid},
    } = useForm<FormData>({
        mode: 'onChange',
        defaultValues: {
            age: undefined,
            therapy_experience: 'false',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            client_gender: 'other',
        },
    });
    const navigate = useNavigate();
    const location = useLocation();
    const [showAgeModal, setShowAgeModal] = useState(false);
    const queryParams = new URLSearchParams(location.search);
    const next = queryParams.get('next') || '/questionnaire/personal';
    const {plt} = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const onSubmit: SubmitHandler<FormData> = (data) => {
        if (data.age >= 18) {
            const existingData = JSON.parse(localStorage.getItem('clientRequest') || '{}');
            const {
                consultation_config: _consultationConfig,
                selected_slot: _selectedSlot,
                psychologist: _psychologist,
                reservation_paid: _reservationPaid,
                ...rest
            } = existingData || {};
            const updatedData = {...rest, ...data};
            localStorage.setItem('clientRequest', JSON.stringify(updatedData));
            localStorage.setItem('timeZone', data.timezone);
            navigate(next);
        } else setShowAgeModal(true);
    };

    useEffect(() => {
        (async () => {
            try {
                const {data} = await axios.get('/api/v1/current_user/');
                console.log(data)
                if (data?.birth_date) {
                    const computedAge = calculateAge(data.birth_date as string)
                    const currentAge = watch('age');
                    console.log(currentAge)
                    if (isNaN(currentAge)) {
                        setValue('age', computedAge, {shouldValidate: true});
                    }
                } else {
                    console.log('no date of birth')
                }
            } catch (e) {
                console.log(e)
            }
        })();
    }, [setValue, watch]);

    useEffect(() => {
        const savedData: Partial<FormData> = JSON.parse(localStorage.getItem('clientRequest') || '{}');
        if (savedData) {
            reset({
                age: savedData.age !== undefined ? savedData.age : undefined,
                therapy_experience: savedData.therapy_experience || 'false',
                timezone: savedData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                client_gender: savedData.client_gender || 'other',
            });
        }
    }, [reset]);

    const age = watch('age', 18);

    return (
        <FC
            id="generalInfoForm"
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            grow
            scroll={'y-auto'}
            px={2}
            mx={'auto'}
            maxW={'700px'}
            h={'100%'}
            ref={containerRef}
        >
            <Head
                title={'Общие вопросы | Лайф Хелп'}
                description={'Помощь квалифицированных психологов от сервиса Лайф Хелп'}
                robots={'noindex, nofollow'}
                canonicalUrl={`${DOMAIN_URL}/questionnaire/general`}
            />
            <FC scroll={'y-auto'} grow pos={'relative'} h={'100%'}>
                <FC g={1} pt={2} scroll={'y-auto'}>

                    <TextField
                        label="Сколько вам лет*"
                        InputLabelProps={{shrink: !!watch('age')}}
                        {...register('age', {
                            required: 'Возраст обязателен',
                            valueAsNumber: true,
                            min: {value: 18, message: 'Возраст не может быть меньше 18'},
                            max: {value: 99, message: 'Слишком большой возраст'},
                            validate: (value) =>
                                /^\d+$/.test(String(value)) || 'Можно вводить только цифры'
                        })}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                'Backspace',
                                'ArrowLeft',
                                'ArrowRight',
                                'ArrowUp',
                                'ArrowDown',
                                'Tab'
                            ];
                            if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        type="number"
                        style={{minWidth: 'max-content'}}
                        error={!!errors.age}
                        helperText={errors.age?.message}
                    />

                    <FormControl component="fieldset" error={!!errors.therapy_experience}>
                        <FormLabel component="legend" className={'mb-1'}>Был ли у вас опыт терапии?*</FormLabel>
                        <Controller
                            name="therapy_experience"
                            control={control}
                            render={({field}) => (
                                <RadioLine
                                    options={[
                                        {value: 'true', label: 'Да'},
                                        {value: 'false', label: 'Нет'},
                                    ]}
                                    selectedValue={field.value || null}
                                    onChange={(value) => field.onChange(value)}
                                />
                            )}
                        />
                        {errors.therapy_experience && <p style={{color: 'red'}}>{errors.therapy_experience.message}</p>}
                    </FormControl>

                    <FormControl component="fieldset" className={'mt-2'} error={!!errors.client_gender}>
                        <FormLabel component="legend" className={'mb-1'}>Пол*</FormLabel>
                        <Controller
                            name="client_gender"
                            control={control}
                            render={({field}) => (
                                <RadioLine
                                    options={[
                                        {value: 'male', label: 'Мужской'},
                                        {value: 'female', label: 'Женский'},
                                        {value: 'other', label: 'Не хочу указывать'},
                                    ]}
                                    className="w-100 fr gap-2 flex-wrap"
                                    itemClass={'flex-grow-1 rounded-3 text-nowrap'}
                                    selectedValue={field.value || null}
                                    onChange={(value) => field.onChange(value)}
                                />
                            )}
                        />
                        {errors.client_gender && <p style={{color: 'red'}}>{errors.client_gender.message}</p>}
                    </FormControl>

                    <FormControl component="fieldset" className={'mt-2'} error={!!errors.timezone}>
                        <Controller
                            name="timezone"
                            control={control}
                            rules={{required: 'Часовой пояс обязателен'}}
                            render={({field}) => (
                                <TimeZonePicker
                                    className={'maxw-200px'}
                                    label="Часовой пояс*"
                                    value={field.value || Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    onChange={(e) => field.onChange(e.target.value)}
                                    name={field.name}
                                    size={'small'}
                                />
                            )}
                        />
                        {errors.timezone && <p style={{color: 'red'}}>{errors.timezone.message}</p>}
                    </FormControl>
                    <Button
                        className={'maxw-340px w-90 mx-auto'} onClick={handleSubmit(onSubmit)}
                        fullWidth variant={'contained'} color="secondary"
                        disabled={!isValid || age < 18 || !watch('client_gender')}
                        sx={{
                            boxShadow: `0 0 20px 10px ${plt.bg.primary}`,
                        }}
                    >
                        Далее
                    </Button>
                </FC>
            </FC>

            <Dialog open={showAgeModal} onClose={() => setShowAgeModal(false)}>
                <DialogTitle>Возрастное ограничение</DialogTitle>
                <DialogContent>
                    К сожалению, вы не можете продолжить, так как ваш возраст меньше 18 лет.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAgeModal(false)}>ОК</Button>
                </DialogActions>
            </Dialog>

            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    "@type": "WebPage",
                    "name": "Подбор психолога онлайн",
                    "url": "https://life-help.ru/questionnaire/general/",
                    "description": "Онлайн психологи Лайф Хелп ждут вас! Заполните анкету, чтобы наш сервис смог подобрать вам подходящего психолога"
                })}
            </script>
        </FC>
    );
};

export default GeneralInfo;
